import type { ComponentType, ReactNode, SVGProps } from 'react';
import {
    ArrowUturnLeftIcon,
    CheckCircleIcon,
    ClockIcon,
    PaperAirplaneIcon,
    PencilIcon,
    XCircleIcon,
} from '@heroicons/react/24/outline';
import type { VATDeclarationApprovalStatus } from '../../../../../../../generated/enums.ts';
import { cn } from '../../../../../../../lib/utils.ts';
import { byName, onDate } from '../utils/index.ts';
import type { VatDeclarationSnapshotSummary } from '../../models.ts';

type ApprovalDisplayStatus = VATDeclarationApprovalStatus | 'not_sent' | 'draft';

interface StatusDisplay {
    label: string;
    icon: ComponentType<SVGProps<SVGSVGElement>>;
    iconClassName: string;
}

const STATUS_DISPLAY: Record<ApprovalDisplayStatus, StatusDisplay> = {
    not_sent: { label: 'Niet verstuurd', icon: PaperAirplaneIcon, iconClassName: 'text-gray-500' },
    pending: { label: 'Wacht op goedkeuring', icon: ClockIcon, iconClassName: 'text-amber-700' },
    approved: { label: 'Goedgekeurd', icon: CheckCircleIcon, iconClassName: 'text-green-600' },
    rejected: { label: 'Afgewezen', icon: XCircleIcon, iconClassName: 'text-red-600' },
    withdrawn: { label: 'Ingetrokken', icon: ArrowUturnLeftIcon, iconClassName: 'text-gray-500' },
    draft: { label: 'Concept', icon: PencilIcon, iconClassName: 'text-gray-500' },
};

type VersionApprovalStatus = Exclude<ApprovalDisplayStatus, 'draft'>;

type StatusSize = 'sm' | 'md';

const DRAFT_TOOLTIP = 'Concept. Deze versie is niet opgeslagen. Je kunt de bedragen nog aanpassen.';

function displayStatus(approval: VatDeclarationSnapshotSummary): VersionApprovalStatus {
    return approval.approval_status ?? 'not_sent';
}

function statusTooltip(status: VersionApprovalStatus, approval: VatDeclarationSnapshotSummary, clientName: string): string {
    const answered = `${byName(approval.approval_answered_by_name)}${onDate(approval.approval_answered_at)}`;
    const tooltips: Record<VersionApprovalStatus, string> = {
        not_sent: `Niet verstuurd. Deze versie is opgeslagen, maar ${clientName} heeft hem niet ter goedkeuring gekregen.`,
        pending: `Wacht op goedkeuring. Verstuurd naar ${clientName}${onDate(approval.approval_requested_at)}. Nog geen antwoord.`,
        approved: `Goedgekeurd${answered}.`,
        rejected: `Afgewezen${answered}.`,
        withdrawn: `Ingetrokken${byName(approval.approval_withdrawn_by_name)}${onDate(approval.approval_withdrawn_at)}.`,
    };
    return tooltips[status];
}

function StatusLabel({ status, tooltip, size }: { status: ApprovalDisplayStatus; tooltip: string; size: StatusSize }) {
    const display = STATUS_DISPLAY[status];
    const Icon = display.icon;
    return (
        <span
            title={tooltip}
            className={cn('inline-flex items-center gap-1.5 text-gray-700', size === 'md' ? 'text-sm' : 'text-xs')}
        >
            <Icon className={cn('shrink-0', size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5', display.iconClassName)} aria-hidden="true" />
            <span>{display.label}</span>
        </span>
    );
}

export function VatReturnApprovalStatusLabel({
    approval,
    clientName,
    size = 'sm',
}: {
    approval: VatDeclarationSnapshotSummary;
    clientName: string;
    size?: StatusSize;
}) {
    const status = displayStatus(approval);
    return <StatusLabel status={status} tooltip={statusTooltip(status, approval, clientName)} size={size} />;
}

export function VatReturnDraftLabel() {
    return <StatusLabel status="draft" tooltip={DRAFT_TOOLTIP} size="sm" />;
}

function StatusIcon({ status, tooltip, isAnnounced }: { status: ApprovalDisplayStatus; tooltip: string; isAnnounced: boolean }) {
    const display = STATUS_DISPLAY[status];
    const Icon = display.icon;
    return (
        <span title={tooltip} className="flex shrink-0">
            <Icon className={cn('h-4 w-4', display.iconClassName)} aria-hidden="true" />
            {isAnnounced && <span className="sr-only">{display.label}</span>}
        </span>
    );
}

export function VatReturnDraftIcon() {
    return <StatusIcon status="draft" tooltip={DRAFT_TOOLTIP} isAnnounced={false} />;
}

export function VatReturnApprovalStatusIcon({ approval, clientName }: { approval: VatDeclarationSnapshotSummary; clientName: string }) {
    const status = displayStatus(approval);
    return <StatusIcon status={status} tooltip={statusTooltip(status, approval, clientName)} isAnnounced />;
}

function Banner({ status, children }: { status: ApprovalDisplayStatus; children: ReactNode }) {
    const display = STATUS_DISPLAY[status];
    const Icon = display.icon;
    return (
        <div className="flex items-start gap-2.5 rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-800">
            <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', display.iconClassName)} aria-hidden="true" />
            <div className="flex grow flex-col gap-1.5">{children}</div>
        </div>
    );
}

export function VatReturnApprovalDetails({ approval, clientName }: { approval: VatDeclarationSnapshotSummary; clientName: string }) {
    switch (approval.approval_status) {
        case null:
            return null;
        case 'pending':
            return (
                <Banner status="pending">
                    <span>
                        {`Verstuurd ter goedkeuring${onDate(approval.approval_requested_at)}. ${clientName} heeft nog niet gereageerd.`}
                    </span>
                </Banner>
            );
        case 'approved':
            return (
                <Banner status="approved">
                    <span>{`Goedgekeurd${byName(approval.approval_answered_by_name)}${onDate(approval.approval_answered_at)}.`}</span>
                </Banner>
            );
        case 'rejected':
            return (
                <Banner status="rejected">
                    <span className="font-semibold">
                        {`Afgewezen${byName(approval.approval_answered_by_name)}${onDate(approval.approval_answered_at)}`}
                    </span>
                    {approval.approval_rejection_reason && (
                        <span className="rounded-md border border-gray-200 bg-white px-3 py-2">
                            {`“${approval.approval_rejection_reason}”`}
                        </span>
                    )}
                </Banner>
            );
        case 'withdrawn':
            return (
                <Banner status="withdrawn">
                    <span>
                        {`Ingetrokken${byName(approval.approval_withdrawn_by_name)}${onDate(approval.approval_withdrawn_at)}. De klant kan dit verzoek niet meer beantwoorden.`}
                    </span>
                </Banner>
            );
        default: {
            const unknown: never = approval.approval_status;
            throw new Error(`Unknown VAT declaration approval status: ${unknown}`);
        }
    }
}
